import { SearchForRetail } from '@/client/api';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
const ANALYTICS_CACHE_TTL_SECONDS = 300; // 5 minutes
/**
 * RTK Query endpoints for rule analytics.
 *
 * Replaces the legacy `rule` Redux slice (`store/reducers/rule.reducers.ts`).
 * Analytics data is fetched via POST and cached for 5 minutes using RTK Query's
 * built-in caching. The response is normalized to a `Record<ruleId, RuleAnalytics>`
 * map for O(1) lookups by individual components (table cells, edit form analytics section).
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const ruleAnalyticsApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches analytics data for the given rule IDs.
         *
         * Uses POST because the request body contains ruleIds + areaId.
         * RTK Query treats this as a query (read-only, cached) despite using POST
         * because the server endpoint is semantically a query, not a mutation.
         *
         * The response array is transformed into a `Record<ruleId, RuleAnalytics>`
         * map via `transformResponse` so each component can do a simple key lookup.
         *
         * Cache key is derived from `serializeQueryArgs` (areaId + sorted ruleIds)
         * to ensure stable caching regardless of ruleIds array order.
         *
         * @endpoint POST /analytics/rules
         * @param args.areaId - The area to fetch analytics for
         * @param args.ruleIds - Array of rule IDs to fetch analytics for
         * @returns Normalized record of rule analytics keyed by ruleId
         */
        getRuleAnalytics: builder.query({
            query: ({ areaId, ruleIds }) => ({
                url: `/${SearchForRetail.ANALYTICS_PATH}/rules`,
                method: 'POST',
                body: { ruleIds, areaId },
            }),
            transformResponse: (response) => {
                var _a;
                const analytics = Array.isArray(response) ? response : (_a = response.result) !== null && _a !== void 0 ? _a : [];
                return Object.fromEntries(analytics.map((item) => [item.ruleId, item]));
            },
            serializeQueryArgs: ({ queryArgs }) => {
                const sortedIds = [...queryArgs.ruleIds].sort();
                return `${queryArgs.areaId}-${sortedIds.join(',')}`;
            },
            providesTags: [CC_API_TAGS.RuleAnalytics],
            keepUnusedDataFor: ANALYTICS_CACHE_TTL_SECONDS,
        }),
    }),
});
export const { useGetRuleAnalyticsQuery, } = ruleAnalyticsApi;
